import React, { useEffect, useReducer, useContext } from "react";
import {
  WmChart,
  WmChartSlice,
  WmSelect,
  WmOption,
  WmButton
} from "@watermarkinsights/ripple-react";
import "../common/Insights.scss";
import { openFlyout } from "../../static/js/commons";
import OpenDrillDownView from "../common/OpenDrillDownView";
import { sendRequest } from "../../common/http";
import { AppContext, AppContextInterface } from "../../contexts/AppContext";
import translate from "@/i18n/translate";
import { useIntl } from "react-intl";
import WidgetLoader from "../common/elements/WidgetLoader";
import ApiError from "../common/elements/ApiError";
import {
  ReportingYear,
  Plan,
  PlanResultSet,
  defaultPlanResultSet,
  defaultPlan,
  Institution,
  DrilldownFilters
} from "../common/interfaces/Interface";
import { checkIfCesProductIntegrated } from "../common/helper";

const ASSESSMENT_PLANNING_ORGANIZATIONS = "assessment_planning_organizations";
const ASSESSMENT_PLANNING_PROGRAMS = "assessment_planning_programs";
const { REDIRECT_URI } = runConfig;

export interface PlanParticipationProps {
  selectedReportingYear: ReportingYear;
  onComponentLoad: (component: string) => void;
  resetLoadedComponent: (component: string) => void;
  productView?: "planning" | "navigator";
}

interface DrilldownData {
  drilldownType: string;
  drilldownStatus: string;
  drilldownTotal: string | JSX.Element;
}

interface PlanParticipationAction {
  type: string;
  chart: ChartData;
  loader: boolean;
  partialLoader: boolean;
  planResultSet: PlanResultSet;
  plan: Plan;
  drilldownData: DrilldownData;
  apiError: boolean;
  hierarchy: any;
}

const defaultChartData = {
  completedCount: 0,
  completedPrgmCount: 0,
  submittedCourseCount: 0,
  inProgressCount: 0,
  inProgressPrgmCount: 0,
  inProgressCourseCount: 0,
  notStartedCount: 0,
  notStartedCourseCount: 0,
  notStartedPrgmCount: 0,
  totalParticipatingOrgs: 0,
  totalParticipatingPrgms: 0,
  totalParticipatingCourses: 0,
  totalPrograms: 0,
  totalOrganizations: 0,
  totalCourses: 0
};

const defaultPlanParticipationAction = {
  type: "",
  chart: defaultChartData,
  loader: false,
  partialLoader: false,
  planResultSet: defaultPlanResultSet,
  plan: defaultPlan,
  drilldownData: {
    drilldownType: "",
    drilldownStatus: "",
    drilldownTotal: ""
  },
  apiError: false,
  hierarchy: {}
};

interface ChartData {
  completedCount: number;
  completedPrgmCount: number;
  submittedCourseCount: number;
  inProgressCount: number;
  inProgressPrgmCount: number;
  inProgressCourseCount: number;
  notStartedCount: number;
  notStartedCourseCount: number;
  notStartedPrgmCount: number;
  totalParticipatingOrgs: number;
  totalParticipatingPrgms: number;
  totalParticipatingCourses: number;
  totalPrograms: number;
  totalOrganizations: number;
  totalCourses: number;
}

interface PlanParticipationState {
  drilldownType: string;
  drilldownTotal: string | JSX.Element;
  drilldownStatus: string;
  selectedPlan: Plan;
  isDrilldownOpen: boolean;
  plans: Plan[];
  chartData: ChartData;
  hierarchy: object | null;
  showTable: boolean;
  drilldownFilters: DrilldownFilters[] | null;
  loader: boolean;
  partialLoader: boolean;
  plansApiError: boolean;
  statsApiError: boolean;
}

export default function PlanParticipation({
  selectedReportingYear,
  onComponentLoad,
  resetLoadedComponent,
  productView = "planning"
}: PlanParticipationProps) {
  const queryString = window.location.search;
  const param = new URLSearchParams(queryString);
  const selectedPlanUuid = param.get("plan_uuid");

  const initialSelectedPlan = {
    uuid: "",
    title: "",
    assessmentTypeId: 0,
    status: 0,
    updatedAt: ""
  };
  const initialState = {
    drilldownType: "",
    drilldownTotal: "",
    drilldownStatus: "any",
    selectedPlan: initialSelectedPlan,
    isDrilldownOpen: false,
    plans: [],
    chartData: {
      completedCount: 0,
      completedPrgmCount: 0,
      submittedCourseCount: 0,
      inProgressCount: 0,
      inProgressPrgmCount: 0,
      inProgressCourseCount: 0,
      notStartedCount: 0,
      notStartedCourseCount: 0,
      notStartedPrgmCount: 0,
      totalParticipatingOrgs: 0,
      totalParticipatingPrgms: 0,
      totalParticipatingCourses: 0,
      totalPrograms: 0,
      totalOrganizations: 0,
      totalCourses: 0
    },
    hierarchy: null,
    showTable: false,
    drilldownFilters: [],
    loader: true,
    partialLoader: true,
    plansApiError: false,
    statsApiError: false
  };

  const defaultDrilldownFilters = (status: number) => {
    let inProgress =
      status === 1
        ? translate("app.drilldown.inComplete")
        : translate("app.drilldown.inProgress");

    return [
      { value: "in_progress", name: inProgress },
      { value: "not_started", name: translate("app.drilldown.notStarted") },
      { value: "submitted", name: translate("app.drilldown.submitted") }
    ];
  };

  const getDrilldownFilters = (drilldownType: string, status: number) => {
    if (drilldownType === ASSESSMENT_PLANNING_ORGANIZATIONS) {
      return null;
    } else {
      return defaultDrilldownFilters(status);
    }
  };

  const getSelectedPlan = (plans: Plan[]) => {
    if (selectedPlanUuid === undefined) {
      return undefined;
    } else {
      const plan = plans.filter((plan: Plan) => plan.uuid === selectedPlanUuid);

      return plan[0];
    }
  };

  const reducer = (
    state: PlanParticipationState,
    action: PlanParticipationAction
  ) => {
    switch (action.type) {
      case "setChartData":
        return { ...state, chartData: action.chart, partialLoader: false };
      case "setLoader":
        return { ...state, loader: action.loader };
      case "setPlanLoader":
        return { ...state, partialLoader: action.partialLoader };
      case "setPlans":
        return {
          ...state,
          plans: action.planResultSet.plans,
          loader: false,
          selectedPlan:
            getSelectedPlan(action.planResultSet.plans) ||
            action.planResultSet.selectedPlan ||
            initialSelectedPlan
        };
      case "setSelectedPlan":
        return { ...state, selectedPlan: action.plan };
      case "openDrilldown":
        return { ...state, isDrilldownOpen: true };
      case "closeDrilldown":
        return { ...state, isDrilldownOpen: false, hierarchy: null };
      case "setDrilldownData":
        return {
          ...state,
          drilldownType: action.drilldownData.drilldownType,
          drilldownTotal: action.drilldownData.drilldownTotal,
          drilldownStatus: action.drilldownData.drilldownStatus,
          drilldownFilters: getDrilldownFilters(
            action.drilldownData.drilldownType,
            state.selectedPlan.status
          )
        };
      case "setHierarchy":
        return { ...state, hierarchy: action.hierarchy, showTable: true };
      case "setPlansApiError":
        return {
          ...state,
          plansApiError: action.apiError,
          loader: false,
          partialLoader: false
        };
      case "setStatsApiError":
        return {
          ...state,
          statsApiError: action.apiError,
          loader: false,
          partialLoader: false
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const { leads, institution, integratedProducts } = useContext<AppContextInterface>(AppContext);

  const openDrilldownView = () => {
    dispatch({ ...defaultPlanParticipationAction, type: "openDrilldown" });
  };
  const closeDrilldown = () => {
    dispatch({ ...defaultPlanParticipationAction, type: "closeDrilldown" });
  };

  const redirect = (url: String) => {
    window.location.replace(`${url}`);
  };

  const resetErrorStates = () => {
    dispatch({
      ...defaultPlanParticipationAction,
      type: "setStatsApiError",
      apiError: false
    });
    dispatch({
      ...defaultPlanParticipationAction,
      type: "setPlansApiError",
      apiError: false
    });
  };

  useEffect(() => {
    resetErrorStates();
    dispatch({
      ...defaultPlanParticipationAction,
      type: "setLoader",
      loader: true
    });
    if (selectedReportingYear && selectedReportingYear.uuid) {
      const applyResult = (result: PlanResultSet) => {
        dispatch({
          ...defaultPlanParticipationAction,
          type: "setPlans",
          planResultSet: {
            plans: result.plans,
            selectedPlan: result.selectedPlan
          }
        });
        if (result.plans.length === 0) {
          onComponentLoad("planParticipationPlans");
          onComponentLoad("planParticipationChartData");
        } else {
          onComponentLoad("planParticipationPlans");
        }
      };
      const onError = () => {
        dispatch({
          ...defaultPlanParticipationAction,
          type: "setPlansApiError",
          apiError: true
        });
        onComponentLoad("planParticipationPlans");
        onComponentLoad("planParticipationChartData");
      };
      sendRequest(
        {
          uri: `/plans?reporting_year_uuid=${selectedReportingYear.uuid}`,
          camelize: true
        },
        applyResult,
        onError
      );
    }
  }, [selectedReportingYear]);

  const { uuid: planUuid, title: selectedPlanTitle } = state.selectedPlan;

  useEffect(() => {
    if (state.plans.length > 0) {
      resetLoadedComponent("planParticipationChartData");
    }
    const applyResult = (chart: ChartData) => {
      dispatch({
        ...defaultPlanParticipationAction,
        type: "setChartData",
        chart: chart
      });

      if (state.plans.length > 0) {
        onComponentLoad("planParticipationChartData");
      }
    };
    const onError = () => {
      dispatch({
        ...defaultPlanParticipationAction,
        type: "setStatsApiError",
        apiError: true
      });
      if (state.plans.length !== 0) {
        onComponentLoad("planParticipationChartData");
      }
    };
    if (planUuid) {
      dispatch({
        ...defaultPlanParticipationAction,
        type: "setPlanLoader",
        partialLoader: true
      });
      sendRequest(
        { uri: `/plan/${planUuid}/stats`, camelize: true },
        applyResult,
        onError
      );
    }
  }, [planUuid]);
  ``;
  const getDrilldownHierarchy = (
    hierarchyURI: string,
    focusAfterbtn: string
  ) => {
    openDrilldownView();
    const applyResult = (result: any) => {
      dispatch({
        ...defaultPlanParticipationAction,
        type: "setHierarchy",
        hierarchy: result
      });
    };
    sendRequest({ uri: hierarchyURI, camelize: true }, applyResult);
  };

  const getDrilldownData = (drillDownNameVal: string) => {
    const {
      totalParticipatingOrgs,
      totalOrganizations,
      totalParticipatingPrgms,
      totalPrograms
    } = state.chartData;

    switch (drillDownNameVal) {
      case ASSESSMENT_PLANNING_ORGANIZATIONS:
        return {
          drilldownTotal: translate(
            "app.planParticipation.ofOrgParticipating",
            {
              totalParticipatingOrgs: totalParticipatingOrgs,
              totalOrganizations: totalOrganizations
            }
          ),
          hierarchyURI: `/hierarchy/participating_organizations/${state.selectedPlan.uuid}`
        };
      case ASSESSMENT_PLANNING_PROGRAMS:
        return {
          drilldownTotal: translate(
            "app.planParticipation.ofPrgmParticipating",
            {
              totalParticipatingPrgms: totalParticipatingPrgms,
              totalPrograms: totalPrograms
            }
          ),
          hierarchyURI: `/hierarchy/participating_programs/${state.selectedPlan.uuid}`
        };
      default:
        return {
          drilldownTotal: "",
          hierarchyURI: ""
        };
    }
  };

  const openDrillDown = (
    drillDownTypeVal: string,
    drilldownStatus: string,
    focusAfterbtn: string
  ) => {
    const drilldownData = getDrilldownData(drillDownTypeVal);
    getDrilldownHierarchy(drilldownData.hierarchyURI, focusAfterbtn);
    dispatch({
      ...defaultPlanParticipationAction,
      type: "setDrilldownData",
      drilldownData: {
        drilldownType: drillDownTypeVal,
        drilldownStatus: drilldownStatus,
        drilldownTotal: drilldownData.drilldownTotal
      }
    });
  };

  const PlanSelect = ({ plans, selectedPlan }: PlanResultSet) => {
    return (
      <WmSelect labelPosition="left" label={useIntl().formatMessage({ id: "app.planParticipation.plan" })} id="filter-by-plan">
        {plans.map((p: Plan) => (
          <WmOption
            aria-label={p.title}
            selected={p.uuid === selectedPlan.uuid}
            onClick={() => {
              dispatch({
                ...defaultPlanParticipationAction,
                type: "setSelectedPlan",
                plan: {
                  uuid: p.uuid,
                  title: p.title,
                  assessmentTypeId: p.assessmentTypeId,
                  status: p.status,
                  updatedAt: ""
                }
              });
            }}
            key={p.uuid}
          >
            {p.title}
          </WmOption>
        ))}
      </WmSelect>
    );
  };

  const getpssUrl = (inst: Institution) => {
    let url = `${REDIRECT_URI}/institution/${inst.pssUuid}/plan/immersive/${planUuid}/node/${inst.rootOrgUuid}/progress?tab=continuous-improvement-insights&reporting_year_uuid=${selectedReportingYear.uuid}&plan_uuid=${planUuid}`;

    if (productView === "navigator") {
      url += `&institution_platform_uuid=${inst.platformUuid}`;
    }

    return url;
  };

  const pssUrl = institution ? getpssUrl(institution) : "";

  const footer = () => {
    return (
      <div className="footer">
        <WmButton
          button-type="textonly"
          onClick={() => institution && redirect(pssUrl)}
        >
          {translate("app.planParticipation.viewMore", {
            selectedPlanTitle: selectedPlanTitle.toString()
          })}
        </WmButton>
      </div>
    );
  };

  const showData = () =>
    !state.loader && !state.partialLoader && !state.statsApiError;
  const showStatsApiError = () =>
    !state.loader && !state.partialLoader && state.statsApiError;
  const showPlansApiError = () =>
    !state.loader && !state.partialLoader && state.plansApiError;

  const RenderAssessmentPlans = () => {
    return (
      <>
        <div className="body">
          <div
            className={
              institution && checkIfCesProductIntegrated(integratedProducts)
                ? ""
                : "plan-container"
            }
          >
            {!state.loader && (
              <>
                <div className="plan-dropdown-container flex-row">
                  <div className="plan-dropdown">
                    <PlanSelect
                      plans={state.plans}
                      selectedPlan={state.selectedPlan}
                    />
                  </div>
                </div>

                <div className="flex-row -justified plan-status-container">
                  <div className="plan-title">{state.selectedPlan.title}</div>
                  <PlanStatus status={state.selectedPlan.status} />
                </div>
              </>
            )}
          </div>
          {showData() && (
            <div className="flex-row insightsgrid">
              {planOrgBar(
                state.chartData,
                state.selectedPlan.assessmentTypeId,
                state.selectedPlan.status
              )}
              {planPrgmBar(
                state.chartData,
                state.selectedPlan.assessmentTypeId,
                state.selectedPlan.status
              )}
              {coursePrgmBar(
                state.chartData,
                state.selectedPlan.assessmentTypeId,
                state.selectedPlan.status
              )}
            </div>
          )}
        </div>
        {showData() && footer()}
        {showStatsApiError() && <ApiError />}
        {(state.loader || state.partialLoader) && (
          <WidgetLoader
            loaderContainerClasses="loader-container"
            loaderClasses="widget-loader"
          />
        )}
      </>
    );
  };

  const notStarted = useIntl().formatMessage({
    id: "app.drilldown.notStarted"
  });
  const inProgress = useIntl().formatMessage({
    id: "app.drilldown.inProgress"
  });
  const inComplete = useIntl().formatMessage({
    id: "app.drilldown.inComplete"
  });

  const submitted = useIntl().formatMessage({
    id: "app.drilldown.submitted"
  });
  const participatingOrgs = useIntl().formatMessage({
    id: "app.planParticipation.participatingOrgs"
  });
  const participatingOrgsNotStarted = useIntl().formatMessage({
    id: "app.planParticipation.participatingOrgsNotStarted"
  });
  const participatingDrilldown = useIntl().formatMessage({
    id: "app.planParticipation.participatingDrilldown"
  });
  const participatingOrgsInProgres = useIntl().formatMessage({
    id: "app.planParticipation.participatingOrgsInProgres"
  });
  const participatingOrgsInComplete = useIntl().formatMessage({
    id: "app.planParticipation.participatingOrgsInComplete"
  });
  const participatingOrgsSubmitted = useIntl().formatMessage({
    id: "app.planParticipation.participatingOrgsSubmitted"
  });

  const participatingCoursesNotStarted = useIntl().formatMessage({
    id: "app.planParticipation.participatingCoursesNotStarted"
  });
  const participatingCoursesInProgress = useIntl().formatMessage({
    id: "app.planParticipation.participatingCoursesInProgress"
  });
  const participatingCoursesInComplete = useIntl().formatMessage({
    id: "app.planParticipation.participatingCoursesInComplete"
  });

  const participatingCoursesSubmitted = useIntl().formatMessage({
    id: "app.planParticipation.participatingCoursesSubmitted"
  });

  const coursePrgmBar = (
    chartData: ChartData,
    assessmentTypeId: number,
    status: number
  ) => {
    if (chartData.totalCourses > 0 && assessmentTypeId === 1) {
      return (
        <div className="flex-row bar-content">
          <div className="counts dropnumber">
            <p className="count">
              {chartData.totalParticipatingCourses.toString() +
                "/" +
                chartData.totalCourses.toString()}
            </p>

            <p className="details">
              {translate("app.planParticipation.courseParticipating")}
            </p>
          </div>
          <div className="chart" id="participating-course-chart">
            <WmChart
              chartType="bar4"
              label={participatingCoursesByStatus}
              valueFormat="amount"
              key="bar4-chart"
              id="prog-status-chart"
            >
              <WmChartSlice
                amount={chartData.notStartedCourseCount.toString()}
                legend={notStarted}
                popover-title={
                  chartData.notStartedCourseCount.toString() +
                  "/" +
                  chartData.totalParticipatingCourses.toString()
                }
                popover-text={participatingCoursesNotStarted}
                key="not_started_count"
              ></WmChartSlice>
              <WmChartSlice
                amount={chartData.inProgressCourseCount.toString()}
                legend={status === 1 ? inComplete : inProgress}
                popover-title={
                  chartData.inProgressCourseCount.toString() +
                  "/" +
                  chartData.totalParticipatingCourses.toString()
                }
                popover-text={
                  status === 1
                    ? participatingCoursesInComplete
                    : participatingCoursesInProgress
                }
                key="in_progress_count"
              ></WmChartSlice>
              <WmChartSlice
                amount={chartData.submittedCourseCount.toString()}
                legend={submitted}
                popover-title={
                  chartData.submittedCourseCount.toString() +
                  "/" +
                  chartData.totalParticipatingCourses.toString()
                }
                popover-text={participatingCoursesSubmitted}
                key="Submitted"
              ></WmChartSlice>
            </WmChart>
          </div>
        </div>
      );
    }
  };

  const planOrgBar = (
    chartData: ChartData,
    assessmentTypeId: number,
    status: number
  ) => {
    if (chartData.totalParticipatingOrgs > 0 && assessmentTypeId === 0) {
      return (
        <div className="flex-row bar-content">
          <div className="counts dropnumber">
            <p className="count">
              {chartData.totalParticipatingOrgs.toString() +
                "/" +
                chartData.totalOrganizations.toString()}
            </p>
            <p className="details">
              {translate("app.planParticipation.orgParticipating")}
            </p>
          </div>
          <div className="chart" id="participating-orgs-chart">
            <WmChart
              chartType="bar4"
              label={participatingOrgs}
              valueFormat="amount"
              key="bar4-chart"
              id="org-status-chart"
            >
              <WmChartSlice
                amount={chartData.notStartedCount.toString()}
                legend={notStarted}
                popover-title={
                  chartData.notStartedCount.toString() +
                  "/" +
                  chartData.totalParticipatingOrgs.toString()
                }
                popover-text={participatingOrgsNotStarted}
                popover-button-text={participatingDrilldown}
                key="not_started_count"
                onClick={() =>
                  openDrillDown(
                    ASSESSMENT_PLANNING_ORGANIZATIONS,
                    "any",
                    "org-status-chart"
                  )
                }
              ></WmChartSlice>
              <WmChartSlice
                amount={chartData.inProgressCount.toString()}
                legend={status === 1 ? inComplete : inProgress}
                popover-title={
                  chartData.inProgressCount.toString() +
                  "/" +
                  chartData.totalParticipatingOrgs.toString()
                }
                popover-text={
                  status === 1
                    ? participatingOrgsInComplete
                    : participatingOrgsInProgres
                }
                popover-button-text={participatingDrilldown}
                key="in_progress_count"
                onClick={() =>
                  openDrillDown(
                    ASSESSMENT_PLANNING_ORGANIZATIONS,
                    "any",
                    "org-status-chart"
                  )
                }
              ></WmChartSlice>
              <WmChartSlice
                amount={chartData.completedCount.toString()}
                legend={submitted}
                popover-title={
                  chartData.completedCount.toString() +
                  "/" +
                  chartData.totalParticipatingOrgs.toString()
                }
                popover-text={participatingOrgsSubmitted}
                popover-button-text={participatingDrilldown}
                key="Submitted"
                onClick={() =>
                  openDrillDown(
                    ASSESSMENT_PLANNING_ORGANIZATIONS,
                    "any",
                    "org-status-chart"
                  )
                }
              ></WmChartSlice>
            </WmChart>
          </div>
        </div>
      );
    }
  };
  const participatingProgramsByStatus = useIntl().formatMessage({
    id: "app.planParticipation.participatingProgramsByStatus"
  });
  const participatingCoursesByStatus = useIntl().formatMessage({
    id: "app.planParticipation.participatingCoursesByStatus"
  });

  const participatingProgramsNotStarted = useIntl().formatMessage({
    id: "app.planParticipation.participatingProgramsNotStarted"
  });
  const participatingDrilldownPrograms = useIntl().formatMessage({
    id: "app.planParticipation.participatingDrilldownPrograms"
  });
  const participatingProgramsInProgress = useIntl().formatMessage({
    id: "app.planParticipation.participatingProgramsInProgress"
  });
  const participatingProgramsInComplete = useIntl().formatMessage({
    id: "app.planParticipation.participatingProgramsInComplete"
  });

  const participatingProgramsCompleted = useIntl().formatMessage({
    id: "app.planParticipation.participatingProgramsSubmitted"
  });

  const planPrgmBar = (
    chartData: ChartData,
    assessmentTypeId: number,
    status: number
  ) => {
    if (chartData.totalParticipatingPrgms > 0 && assessmentTypeId === 0) {
      return (
        <div className="flex-row bar-content">
          <div className="counts dropnumber">
            <p className="count">
              {chartData.totalParticipatingPrgms.toString() +
                "/" +
                chartData.totalPrograms.toString()}
            </p>
            <p className="details">
              {translate("app.planParticipation.programsParticipating")}
            </p>
          </div>
          <div className="chart" id="participating-prgrm-chart">
            <WmChart
              chartType="bar4"
              label={participatingProgramsByStatus}
              valueFormat="amount"
              key="bar4-chart"
              id="prog-status-chart"
            >
              <WmChartSlice
                amount={chartData.notStartedPrgmCount.toString()}
                legend={notStarted}
                popover-title={
                  chartData.notStartedPrgmCount.toString() +
                  "/" +
                  chartData.totalParticipatingPrgms.toString()
                }
                popover-text={participatingProgramsNotStarted}
                popover-button-text={participatingDrilldownPrograms}
                key="not_started_count"
                onClick={() =>
                  openDrillDown(
                    ASSESSMENT_PLANNING_PROGRAMS,
                    "not_started",
                    "prog-status-chart"
                  )
                }
              ></WmChartSlice>
              <WmChartSlice
                amount={chartData.inProgressPrgmCount.toString()}
                legend={status === 1 ? inComplete : inProgress}
                popover-title={
                  chartData.inProgressPrgmCount.toString() +
                  "/" +
                  chartData.totalParticipatingPrgms.toString()
                }
                popover-text={
                  status === 1
                    ? participatingProgramsInComplete
                    : participatingProgramsInProgress
                }
                popover-button-text={participatingDrilldownPrograms}
                key="in_progress_count"
                onClick={() =>
                  openDrillDown(
                    ASSESSMENT_PLANNING_PROGRAMS,
                    "in_progress",
                    "prog-status-chart"
                  )
                }
              ></WmChartSlice>
              <WmChartSlice
                amount={chartData.completedPrgmCount.toString()}
                legend={submitted}
                popover-title={
                  chartData.completedPrgmCount.toString() +
                  "/" +
                  chartData.totalParticipatingPrgms.toString()
                }
                popover-text={participatingProgramsCompleted}
                popover-button-text={participatingDrilldownPrograms}
                key="Submitted"
                onClick={() =>
                  openDrillDown(
                    ASSESSMENT_PLANNING_PROGRAMS,
                    "submitted",
                    "prog-status-chart"
                  )
                }
              ></WmChartSlice>
            </WmChart>
          </div>
        </div>
      );
    }
  };

  const getURL = () => {
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const locale = param.get("locale");
    const isPlanning = window.location.pathname.includes("/planning/");
    const base = isPlanning ? `/insights/planning` : `/insights`;
    const institutionUuid = institution?.platformUuid ?? "";
    if (locale === null) {
      return `${base}/institution/${institutionUuid}/reporting_year/${selectedReportingYear.uuid}/plan/${state.selectedPlan.uuid}/html`;
    } else {
      return `${base}/institution/${institutionUuid}/reporting_year/${selectedReportingYear.uuid}/plan/${state.selectedPlan.uuid}/html?locale=${locale}`;
    }
  };

  const openPlanHTMLViewLink = () => {
    return window.open(getURL(), "_blank");
  };

  const header = () => {
    return (
      <div className="header">
        <div className="info">
          <h3 className="title">
            {translate("app.planParticipation.participationAssessment")}
          </h3>
          <p className="description">
            {translate("app.planParticipation.reviewEngagement")}
          </p>
        </div>
        <div>
          {showData() && state.plans.length > 0 && (
            <WmButton
              id="export-button"
              tabIndex={0}
              onClick={openPlanHTMLViewLink}
              button-type="secondary"
            >
              {translate("app.exportHtml")}
            </WmButton>
          )}
        </div>
      </div>
    );
  };
  const planParticipationBody = () => {
    return (
      <div className="paper-card paper-card-height">
        {header()}
        {state.loader && (
          <WidgetLoader
            loaderContainerClasses="loader"
            loaderClasses="content"
          />
        )}
        {!state.loader && (
          <>
            {state.plans.length > 0 && <RenderAssessmentPlans />}
            {state.plans.length === 0 && !showPlansApiError() && <NoPlans />}
            {showPlansApiError() && <ApiError />}
          </>
        )}
      </div>
    );
  };
  const DRILLDOWN_SUB_HEADER = useIntl().formatMessage({
    id: "app.planParticipation.subHeader"
  });

  return (
    <>
      {planParticipationBody()}

        <OpenDrillDownView
          setIsDrilldownOpen={closeDrilldown}
          drilldownType={state.drilldownType}
          total={state.drilldownTotal}
          showTable={state.showTable}
          drilldownStatus={state.drilldownStatus}
          completeHierarchy={state.hierarchy}
          subHeader={DRILLDOWN_SUB_HEADER}
          filters={state.drilldownFilters}
          title={state.selectedPlan.title}
          leads={leads}
          planStatus={state.selectedPlan.status}
          isVisible={state.isDrilldownOpen}
          open={state.isDrilldownOpen}
          id="plan-participation-drill-down-view"
        ></OpenDrillDownView>
    </>
  );
}

const PlanStatus = ({ status }: { status: number }) => {
  if (status === 1)
    return (
      <div className="success-status -completed">
        <div className="title-text">{translate("app.drilldown.completed")}</div>
      </div>
    );

  return (
    <div className="in-progress-status-export">
      <div className="title-text">{translate("app.drilldown.inProgress")}</div>
    </div>
  );
};

const NoPlans = () => {
  return (
    <div className="no-plans">
      <div className="no-plans-text">
        {translate(
          "app.continuousImprovement.planningAndParticipation.noPlansText"
        )}
      </div>
      <div className="no-plans-text-select">
        {translate(
          "app.continuousImprovement.planningAndParticipation.noPlansSubText"
        )}
      </div>
    </div>
  );
};
